/**
 * Componente: Etapa 2 - Informações do Equipamento e Confirmação
 * 
 * Este componente exibe e permite editar informações do equipamento selecionado,
 * incluindo endereço, último atendimento e laudo.
 * Migrado incrementalmente de App.js
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAddressSearch } from '../hooks/useAddressSearch';
import { useDefeitoSearch } from '../hooks/useDefeitoSearch';
import { formatDateToPTBR, translatePreventivaType, applyPhoneMask, formatNumberWithMask, removeMask, getLocalDateString, getTodayLocal, getNextBusinessDay } from '../utils/formatters';
import {
  validateEmail as validateEmailFormat,
  validateRequiredFieldsWithErrors,
  validateBaseOsFields,
} from '../utils/validators';
import { getAllowedTipoValuesForAbertura, isTipoAllowedForDefeito } from '../utils/defeitoTipoRules';
import { getCounters, getTB02112Field } from '../services/equipmentService';
import TipoOSOptionsButton from './TipoOSOptionsButton';
import { BookSearch, CheckCircle2, Mic, Type, X } from 'lucide-react';
import { useComposerVoiceRecorder } from '../../board-chat/components/ComposerVoiceRecorder';
import { BoardChatAiWriteMenu } from '../../board-chat/components/BoardChatAiWriteMenu';
import { BchTipLayer, useBchTip } from '../../board-chat/components/BchFloatTip';

const EquipmentInfoStep = ({
  selectedEquipment,
  setSelectedEquipment,
  lastOsDate,
  lastOsData,
  isLoadingLastOs,
  showLaudo,
  setShowLaudo,
  onEquipmentUpdate,
  showDefeitoDetails,
  setShowDefeitoDetails,
  showLastOsSection,
  setShowLastOsSection,
  // Props para SOLICITANTE, CONTATO, EMAIL, DEFEITO
  solicitante,
  setSolicitante,
  contatoFone1,
  setContatoFone1,
  contatoWhatsApp,
  setContatoWhatsApp,
  contatoFone2,
  setContatoFone2,
  email,
  setEmail,
  emailError,
  setEmailError,
  selectedDefeito,
  setSelectedDefeito,
  defeitoObservacao,
  setDefeitoObservacao,
  // Props para Contadores
  showCounters,
  setShowCounters,
  countersConfirmed,
  setCountersConfirmed,
  loadingCounters,
  setLoadingCounters,
  lastCounters,
  setLastCounters,
  counterValues,
  setCounterValues,
  counterErrors,
  setCounterErrors,
  // Props para botões de ação
  validateRequiredFields,
  isScheduled,
  scheduleData,
  showScheduleTooltip,
  setShowScheduleTooltip,
  showScheduleDropdown,
  setShowScheduleDropdown,
  showScheduleSidebar,
  setShowScheduleSidebar,
  scheduleType,
  setScheduleType,
  selectedScheduleDate,
  setSelectedScheduleDate,
  setScheduleNoCertainDate,
  setFuturaStandbyNotes,
  handleLoadScheduleData,
  handleCreateOS,
  isCreatingOS = false,
  osType = 'contrato', // 'contrato' ou 'estoque'
  // Props para modo contrato
  isContractMode = false,
  selectedEquipments = [],
  hasMultipleSites = false,
  // Tipo de OS (somente modo único: serial/patrimônio)
  tipoOsUniqueValue = 'N',
  setTipoOsUniqueValue,
  tipoOsUniqueConfirmed = false,
  setTipoOsUniqueConfirmed,
  defeitoVinculoEquipAtivo = false,
  tipoOsValidationExtras = null
}) => {
  const {
    isEditingAddress,
    setIsEditingAddress,
    addressSearch,
    setAddressSearch,
    addressSuggestions,
    setAddressSuggestions,
    isLoadingAddress,
    setCorrectedAddress,
    originalAddress,
    setOriginalAddress
  } = useAddressSearch();

  const [isEditingLocalInterno, setIsEditingLocalInterno] = useState(false);
  const [localInternoDraft, setLocalInternoDraft] = useState('');
  const [localShowLastOsSection, setLocalShowLastOsSection] = useState(false);
  const [localShowDefeitoDetails, setLocalShowDefeitoDetails] = useState(false);
  const [showEquipmentsModal, setShowEquipmentsModal] = useState(false);
  const [contatoType, setContatoType] = useState('fone1'); // 'fone1' | 'whatsapp' | 'fone2'
  const [contatoDropdownOpen, setContatoDropdownOpen] = useState(false);
  const contatoDropdownRef = useRef(null);

  const [tb02112AutoFillLoading, setTb02112AutoFillLoading] = useState({
    solicitante: false,
    contato: false,
    email: false
  });

  const [tb02112AutoFillConfirmed, setTb02112AutoFillConfirmed] = useState(() => ({
    solicitante: Boolean(solicitante && String(solicitante).trim()),
    contato: Boolean(contatoFone1 && String(contatoFone1).trim()),
    email: Boolean(email && String(email).trim())
  }));

  useEffect(() => {
    setTb02112AutoFillConfirmed({
      solicitante: Boolean(solicitante && String(solicitante).trim()),
      contato: Boolean(contatoFone1 && String(contatoFone1).trim()),
      email: Boolean(email && String(email).trim())
    });
  }, [solicitante, contatoFone1, email]);

  const handleTB02112AutoFill = async (key) => {
    const serie = selectedEquipment?.serie;
    const contrato = selectedEquipment?.codigo;
    if (!serie || !contrato) return;
    if (tb02112AutoFillLoading[key]) return;

    const apiField = key === 'solicitante' ? 'SOLICITANTE' : key === 'contato' ? 'CONTATO' : 'EMAIL';

    setTb02112AutoFillLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const result = await getTB02112Field(serie, contrato, apiField);
      const value = result?.value;

      if (value !== null && value !== undefined && String(value).trim() !== '') {
        if (key === 'solicitante') {
          setSolicitante && setSolicitante(value);
        } else if (key === 'contato') {
          setContatoType && setContatoType('fone1');
          setContatoFone1 && setContatoFone1(applyPhoneMask(String(value)));
        } else if (key === 'email') {
          setEmail && setEmail(String(value));
          if (setEmailError) setEmailError('');
        }

        setTb02112AutoFillConfirmed((prev) => ({ ...prev, [key]: true }));
      }
    } finally {
      setTb02112AutoFillLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const TB02112IconButton = ({ fieldKey, disabledTitle }) => {
    const loading = tb02112AutoFillLoading[fieldKey];
    const confirmed = tb02112AutoFillConfirmed[fieldKey];

    return (
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleTB02112AutoFill(fieldKey)}
        disabled={loading}
        title={disabledTitle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '14px',
          height: '14px',
          padding: 0,
          marginLeft: '6px',
          background: 'transparent',
          border: 'none',
          cursor: loading ? 'progress' : 'pointer',
          transition: 'transform 0.18s ease, opacity 0.18s ease, color 0.18s ease',
          opacity: confirmed ? 1 : loading ? 0.7 : 0.95
        }}
      >
        {confirmed ? (
          <CheckCircle2 size={12} color="#22c55e" />
        ) : (
          <BookSearch size={12} color="#7c4dff" />
        )}
      </button>
    );
  };

  const tipoOsValue = tipoOsUniqueValue;

  const codEquipModelo = useMemo(
    () => String(selectedEquipment?.codprod || selectedEquipment?.codProduto || '').trim(),
    [selectedEquipment]
  );

  const formValidationExtras = tipoOsValidationExtras || {};

  const allowedTiposParaDefeito =
    defeitoVinculoEquipAtivo && selectedDefeito
      ? getAllowedTipoValuesForAbertura(selectedDefeito, {
        restrictionActive: true,
        origem: osType === 'estoque' ? 'estoque' : 'contrato',
      })
      : null;

  const obsOkTipo = (defeitoObservacao || '').trim().length >= 15;
  const tipoOsNeedsAttention =
    defeitoVinculoEquipAtivo &&
    !isContractMode &&
    osType !== 'estoque' &&
    selectedDefeito &&
    obsOkTipo &&
    (!tipoOsValue || !isTipoAllowedForDefeito(selectedDefeito, tipoOsValue));

  const baseFormValid = validateBaseOsFields(solicitante, contatoFone1, selectedDefeito, defeitoObservacao);

  // Ref para o input de endereço (para foco automático)
  const addressInputRef = useRef(null);
  
  // Ref para o textarea de observação do defeito (para foco automático)
  const defeitoObservacaoRef = useRef(null);
  const [obsVoiceError, setObsVoiceError] = useState('');
  const [obsAiWriteOpen, setObsAiWriteOpen] = useState(false);
  const { tip: obsTip, tipProps: obsTipProps, hideTip: hideObsTip } = useBchTip();
  const obsVoice = useComposerVoiceRecorder({
    onError: (msg) => setObsVoiceError(msg || 'Falha no microfone'),
  });
  
  // Ref para o input SOLICITANTE (foco ao entrar no formulário)
  const solicitanteInputRef = useRef(null);

  // Fechar dropdown de contato ao clicar fora
  useEffect(() => {
    if (!contatoDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (contatoDropdownRef.current && !contatoDropdownRef.current.contains(e.target)) {
        setContatoDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contatoDropdownOpen]);

  // Focar automaticamente no SOLICITANTE ao entrar no formulário (primeiro campo)
  useEffect(() => {
    if (selectedEquipment && solicitante !== undefined && solicitanteInputRef.current) {
      const t = setTimeout(() => solicitanteInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [selectedEquipment]);

  // Focar automaticamente no input quando isEditingAddress mudar para true
  useEffect(() => {
    if (isEditingAddress && addressInputRef.current) {
      // Pequeno delay para garantir que o input está renderizado
      setTimeout(() => {
        addressInputRef.current?.focus();
      }, 100);
    }
  }, [isEditingAddress]);
  
  // Focar automaticamente no textarea quando um defeito for selecionado
  useEffect(() => {
    if (selectedDefeito && defeitoObservacaoRef.current) {
      // Pequeno delay para garantir que o textarea está renderizado
      setTimeout(() => {
        defeitoObservacaoRef.current?.focus();
      }, 100);
    }
  }, [selectedDefeito]);

  useEffect(() => {
    if (selectedDefeito) return undefined;
    setObsVoiceError('');
    setObsAiWriteOpen(false);
    obsVoice.cancel();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao limpar o defeito
  }, [selectedDefeito]);

  const handleObsTranscribe = async () => {
    setObsVoiceError('');
    try {
      if (!obsVoice.speechSupported) {
        await obsVoice.cancel();
        setObsVoiceError('Transcrição não suportada neste navegador.');
        return;
      }
      const text = String((await obsVoice.finishTranscript()) || '').trim();
      if (!text) {
        setObsVoiceError('Não foi possível obter o texto desta gravação.');
        return;
      }
      if (typeof setDefeitoObservacao !== 'function') return;
      setDefeitoObservacao((prev) => {
        const cur = String(prev || '').trim();
        return cur ? `${cur} ${text}` : text;
      });
    } catch (e) {
      setObsVoiceError(e.message || 'Não foi possível obter o texto desta gravação.');
    }
  };

  
  // Usar prop se disponível, senão usar estado local
  const defeitoDetailsState = showDefeitoDetails !== undefined ? showDefeitoDetails : localShowDefeitoDetails;
  const setDefeitoDetailsState = setShowDefeitoDetails || setLocalShowDefeitoDetails;
  const lastOsSectionState = showLastOsSection !== undefined ? showLastOsSection : localShowLastOsSection;
  const setLastOsSectionState = setShowLastOsSection || setLocalShowLastOsSection;

  // Hook para busca de defeitos (filtro por modelo no backend quando a config estiver ativa)
  const {
    defeitoSearch,
    setDefeitoSearch,
    defeitoSuggestions,
    isLoadingDefeitos,
    showSolucaoTooltip,
    setShowSolucaoTooltip
  } = useDefeitoSearch({ codEquip: codEquipModelo || null });

  useEffect(() => {
    if (!defeitoVinculoEquipAtivo || !selectedDefeito || !setTipoOsUniqueValue) return;
    const allowed = getAllowedTipoValuesForAbertura(selectedDefeito, {
      restrictionActive: true,
      origem: osType === 'estoque' ? 'estoque' : 'contrato',
    });
    if (tipoOsValue && !allowed.includes(tipoOsValue)) {
      setTipoOsUniqueValue('');
      if (setTipoOsUniqueConfirmed) setTipoOsUniqueConfirmed(false);
    }
  }, [
    defeitoVinculoEquipAtivo,
    selectedDefeito,
    tipoOsValue,
    osType,
    setTipoOsUniqueValue,
    setTipoOsUniqueConfirmed,
  ]);

  // Handler para validação de e-mail
  const handleEmailChange = (value) => {
    if (setEmail) setEmail(value);
    if (setEmailError) {
      if (value) {
        if (!validateEmailFormat(value)) {
          setEmailError('E-mail inválido');
        } else {
          setEmailError('');
        }
      } else {
        setEmailError('');
      }
    }
  };

  // Handler para seleção de defeito
  const handleDefeitoSelect = (defeito) => {
    if (setSelectedDefeito) setSelectedDefeito(defeito);
    if (setDefeitoSearch) setDefeitoSearch(defeito.nome);
    // Limpar sugestões após seleção
    setTimeout(() => {
      // As sugestões são gerenciadas pelo hook
    }, 100);
  };

  // Handler para limpar defeito selecionado
  const handleDefeitoClear = () => {
    if (setSelectedDefeito) setSelectedDefeito(null);
    if (setDefeitoObservacao) setDefeitoObservacao('');
    if (setDefeitoSearch) setDefeitoSearch('');
    if (setTipoOsUniqueValue) setTipoOsUniqueValue('N');
    if (setTipoOsUniqueConfirmed) setTipoOsUniqueConfirmed(false);
  };

  // Handler para buscar contadores do equipamento
  const handleLoadCounters = async () => {
    if (!selectedEquipment || !selectedEquipment.serie) {
      if (setLastCounters) setLastCounters({ pb: 0, color: 0, total: 0 });
      return;
    }

    if (setLoadingCounters) setLoadingCounters(true);
    try {
      const result = await getCounters(selectedEquipment.serie);
      if (result.success) {
        const pb = result.ContadorPB != null ? Number(result.ContadorPB) : 0;
        const color = result.ContadorCor != null ? Number(result.ContadorCor) : 0;
        const total = result.ContadorTotal != null ? Number(result.ContadorTotal) : 0;
        if (setLastCounters) setLastCounters({ pb, color, total });
      } else {
        if (setLastCounters) setLastCounters({ pb: 0, color: 0, total: 0 });
      }
    } catch (error) {
      console.error('Erro ao buscar contadores:', error);
      if (setLastCounters) setLastCounters({ pb: 0, color: 0, total: 0 });
    } finally {
      if (setLoadingCounters) setLoadingCounters(false);
    }
  };

  // Handler para abrir formulário de contadores
  const handleOpenCounters = async () => {
    // Minimizar "ÚLTIMO ATENDIMENTO" quando abrir contadores
    if (setShowLastOsSection) setShowLastOsSection(false);
    if (setShowCounters) setShowCounters(true);
    if (setCountersConfirmed) setCountersConfirmed(false);
    if (setCounterErrors) setCounterErrors({ pb: '', color: '', total: '' });
    await handleLoadCounters();
  };

  // Handler para mudança de valor de contador
  const handleCounterChange = (type, value) => {
    const rawValue = removeMask(value);
    if (setCounterValues) setCounterValues(prev => ({ ...prev, [type]: rawValue }));
    if (setCounterErrors) setCounterErrors(prev => ({ ...prev, [type]: '' }));
  };

  // Handler para validação ao perder foco
  const handleCounterBlur = (type) => {
    if (!counterValues || !lastCounters) return;
    const raw = removeMask(counterValues[type] || '').trim();
    if (!raw) return;
    
    const num = Number(raw);
    const last = Number(lastCounters[type] ?? 0);
    
    if (!Number.isNaN(num) && num < last && setCounterErrors) {
      setCounterErrors(prev => ({
        ...prev,
        [type]: 'O contador é menor do que o último registrado em sistema.'
      }));
    }
  };

  // Handler para confirmar contadores
  const handleConfirmCounters = () => {
    if (!counterValues || !lastCounters || !setCountersConfirmed) return;
    
    // Normalizar vazios para placeholder ou zero
    const pbValue = counterValues.pb?.trim() !== ''
      ? Number(counterValues.pb)
      : Number(lastCounters.pb ?? 0);
    const colorValue = counterValues.color?.trim() !== ''
      ? Number(counterValues.color)
      : Number(lastCounters.color ?? 0);
    const totalValue = counterValues.total?.trim() !== ''
      ? Number(counterValues.total)
      : Number(lastCounters.total ?? 0);

    const newErrors = { pb: '', color: '', total: '' };
    const lastPb = Number(lastCounters.pb ?? 0);
    const lastColor = Number(lastCounters.color ?? 0);
    const lastTotal = Number(lastCounters.total ?? 0);

    // Validar se algum contador é menor que o último registrado
    if (pbValue < lastPb) {
      newErrors.pb = 'O contador é menor do que o último registrado em sistema.';
    }
    if (colorValue < lastColor) {
      newErrors.color = 'O contador é menor do que o último registrado em sistema.';
    }
    if (totalValue < lastTotal) {
      newErrors.total = 'O contador é menor do que o último registrado em sistema.';
    }

    // Se há erros, não confirmar
    if (newErrors.pb || newErrors.color || newErrors.total) {
      if (setCounterErrors) setCounterErrors(newErrors);
      return;
    }

    // Confirmar contadores
    setCountersConfirmed(true);
    if (setCounterErrors) setCounterErrors({ pb: '', color: '', total: '' });
  };

  // Handler para atualizar endereço
  const handleAddressSelect = (suggestion) => {
    if (!originalAddress) {
      setOriginalAddress(selectedEquipment.Endereco);
    }
    
    // Extrair campos individuais do endereço da API Nominatim
    const address = suggestion.address || {};
    
    // Extrair número do logradouro com múltiplas tentativas (prioridade)
    let houseNumber = null;
    
    // 1. Tentar usar número extraído e preservado no suggestion
    if (suggestion.extractedNumber) {
      houseNumber = suggestion.extractedNumber;
    }
    // 2. Tentar pegar diretamente do address da API
    else if (address.house_number || address.house_name) {
      houseNumber = address.house_number || address.house_name;
      if (houseNumber && houseNumber.toUpperCase() === 'S/N') {
        houseNumber = '0';
      }
    }
    // 3. Tentar extrair do display (ex: "Rua das Flores, 123")
    else if (suggestion.display) {
      const displayMatch = suggestion.display.match(/,\s*(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
      if (displayMatch) {
        houseNumber = displayMatch[1].toUpperCase() === 'S/N' ? '0' : displayMatch[1];
      }
    }
    // 4. Tentar extrair do original
    if (!houseNumber && suggestion.original) {
      const originalMatch = suggestion.original.match(/,\s*(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
      if (originalMatch) {
        houseNumber = originalMatch[1].toUpperCase() === 'S/N' ? '0' : originalMatch[1];
      }
    }
    // 5. Tentar extrair da query do usuário (addressSearch)
    if (!houseNumber && addressSearch) {
      const queryMatch = addressSearch.match(/\b(\d+[A-Za-z]?|S\/N|s\/n)\b/i);
      if (queryMatch) {
        houseNumber = queryMatch[1].toUpperCase() === 'S/N' ? '0' : queryMatch[1];
      }
    }
    
    // Se ainda não encontrou, usar '0' (sem número)
    if (!houseNumber) {
      houseNumber = '0';
    }
    
    const updatedEquipment = {
      ...selectedEquipment,
      Endereco: suggestion.display.toUpperCase(),
      // Armazenar campos individuais do endereço editado
      corrected_end_logradouro: address.road || address.street || address.pedestrian || '',
      corrected_end_num: houseNumber,
      corrected_end_comp: address.unit || address.level || '',
      corrected_end_bairro: address.suburb || address.neighbourhood || address.quarter || address.city_district || '',
      corrected_end_cidade: address.city || address.town || address.village || address.municipality || '',
      corrected_end_estado: address.state ? address.state.substring(0, 2).toUpperCase() : '',
      corrected_end_cep: address.postcode || ''
    };
    setSelectedEquipment(updatedEquipment);
    setIsEditingAddress(false);
    setAddressSearch('');
    setAddressSuggestions([]);
    if (onEquipmentUpdate) {
      onEquipmentUpdate(updatedEquipment);
    }
  };

  // Handler para atualizar local interno
  const handleLocalInternoUpdate = (value) => {
    const updatedEquipment = {
      ...selectedEquipment,
      Endereco_Local: value
    };
    setSelectedEquipment(updatedEquipment);
    if (onEquipmentUpdate) {
      onEquipmentUpdate(updatedEquipment);
    }
  };

  if (!selectedEquipment) {
    return null;
  }

  return (
    <div style={{
      background: 'var(--modal-bg, #ffffff)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid var(--border-subtle, #e8e8e8)',
      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
      flex: '1 1 auto',
      minWidth: '400px'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
        color: 'var(--text-body, #2d3748)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        Confirmação de Informações
      </h3>

      {/* Equipamento e Série/Patrimônio lado a lado */}
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        marginBottom: '16px'
      }}>
        {/* Equipamento */}
        <div style={{ flex: 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Equipamento
          </label>
          {isContractMode && selectedEquipments && selectedEquipments.length > 1 ? (
            <div 
              onClick={() => setShowEquipmentsModal(true)}
              style={{
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: '#7c4dff',
                cursor: 'pointer',
                textDecoration: 'underline',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#5a3dc4';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#7c4dff';
              }}
            >
              Exibir {selectedEquipments.length} equipamentos
            </div>
          ) : (
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-body, #2d3748)'
            }}>
              {selectedEquipment.produto}
              {selectedEquipment.dtinstalacao && (
                <span 
                  title="Data de instalação"
                  style={{ 
                    fontSize: '12px', 
                    fontWeight: 400, 
                    color: 'var(--text-muted, #718096)',
                    marginLeft: '8px',
                    cursor: 'help'
                  }}>
                  ({formatDateToPTBR(selectedEquipment.dtinstalacao)})
                </span>
              )}
            </div>
          )}
        </div>

        {/* Série e Patrimônio */}
        <div style={{ flex: 1 }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Série / Patrimônio
          </label>
          {isContractMode && selectedEquipments && selectedEquipments.length > 1 ? (
            <div 
              onClick={() => setShowEquipmentsModal(true)}
              style={{
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: '#7c4dff',
                cursor: 'pointer',
                textDecoration: 'underline',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#5a3dc4';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#7c4dff';
              }}
            >
              Exibir {selectedEquipments.length} séries
            </div>
          ) : (
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-body, #2d3748)'
            }}>
              {selectedEquipment.serie} {selectedEquipment.patrimonio && `• ${selectedEquipment.patrimonio}`}
            </div>
          )}
        </div>
      </div>

      {/* Separador */}
      <div style={{
        height: '1px',
        background: 'var(--border-subtle, #e8e8e8)',
        margin: '0 0 16px 0'
      }}></div>

      {/* Cliente */}
      {selectedEquipment.cliente && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Cliente
          </label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-body, #2d3748)'
            }}>
              {selectedEquipment.codigo ? `${selectedEquipment.codigo} ` : ''}
              {selectedEquipment.cliente}
            </div>
            {/* Aviso de Contrato Vencido */}
            {selectedEquipment.contr_venc && (() => {
              const vencDate = new Date(selectedEquipment.contr_venc);
              const today = new Date();
              vencDate.setHours(0, 0, 0, 0);
              today.setHours(0, 0, 0, 0);
              return vencDate < today ? (
                <div
                  className="open-os-badge-expired"
                  style={{
                  fontSize: '10px',
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: '#c53030',
                  fontWeight: 600,
                  padding: '2px 6px',
                  background: '#fff5f5',
                  border: '1px solid #feb2b2',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap'
                }}>
                  Contrato vencido
                </div>
              ) : null;
            })()}
          </div>
        </div>
      )}

      {/* Endereço de Instalação */}
      {selectedEquipment.Endereco && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '4px',
            fontSize: '11px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #718096)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Endereço de Instalação
          </label>
          {!isEditingAddress ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: 'var(--text-body, #2d3748)',
                flex: '1 1 auto',
                textTransform: originalAddress ? 'uppercase' : 'none'
              }}>
                {selectedEquipment.Endereco}
                {osType !== 'estoque' && !(isContractMode && hasMultipleSites) && selectedEquipment?.Endereco_Local && selectedEquipment.Endereco_Local.trim() && !isEditingLocalInterno && (
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: 400, 
                    color: 'var(--text-muted, #718096)',
                    marginLeft: '8px',
                    textTransform: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    ({selectedEquipment.Endereco_Local})
                    <i 
                      className="bi bi-pencil"
                      style={{ 
                        fontSize: '11px', 
                        color: '#7c4dff',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                      setLocalInternoDraft(selectedEquipment?.Endereco_Local || '');
                      setIsEditingLocalInterno(true);
                    }}
                      onMouseEnter={(e) => {
                        e.target.style.color = '#5a3dc4';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = '#7c4dff';
                      }}
                    ></i>
                  </span>
                )}
                {osType !== 'estoque' && !(isContractMode && hasMultipleSites) && (!selectedEquipment?.Endereco_Local || !selectedEquipment.Endereco_Local.trim()) && !isEditingLocalInterno && (
                  <button
                    onClick={() => {
                      setLocalInternoDraft(selectedEquipment?.Endereco_Local || '');
                      setIsEditingLocalInterno(true);
                    }}
                    style={{
                      fontSize: '11px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: '#7c4dff',
                      background: 'transparent',
                      border: '1px solid #7c4dff',
                      borderRadius: '4px',
                      padding: '2px 6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      marginLeft: '8px',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#7c4dff';
                      e.target.style.color = '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#7c4dff';
                    }}
                  >
                    + Localização
                  </button>
                )}
                {osType !== 'estoque' && !(isContractMode && hasMultipleSites) && isEditingLocalInterno && (
                  <div style={{ 
                    marginLeft: '8px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <input
                      type="text"
                      value={localInternoDraft}
                      onChange={(e) => setLocalInternoDraft(e.target.value)}
                      placeholder={selectedEquipment?.Endereco_Local && selectedEquipment.Endereco_Local.trim() ? "Atualize o local interno" : "Informe o local interno"}
                      autoFocus
                      style={{
                        width: '200px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        border: '1px solid #7c4dff',
                        borderRadius: '6px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        color: 'var(--text-body, #2d3748)',
                        background: 'var(--modal-bg, #ffffff)'
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)';
                        handleLocalInternoUpdate(localInternoDraft);
                        setIsEditingLocalInterno(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === 'Escape') {
                          if (e.key === 'Enter') handleLocalInternoUpdate(localInternoDraft);
                          setIsEditingLocalInterno(false);
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        handleLocalInternoUpdate(localInternoDraft);
                        setIsEditingLocalInterno(false);
                      }}
                      style={{
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: 'var(--text-muted, #718096)',
                        background: 'transparent',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '4px',
                        padding: '2px 6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                      }}
                    >
                      ✓
                    </button>
                  </div>
                )}
              </div>
              {originalAddress && originalAddress !== selectedEquipment.Endereco && (
                <span
                  title={originalAddress}
                  style={{
                    fontSize: '10px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    fontWeight: 500,
                    color: '#48bb78',
                    background: '#f0fff4',
                    border: '1px solid #9ae6b4',
                    borderRadius: '4px',
                    padding: '3px 8px',
                    cursor: 'help',
                    whiteSpace: 'nowrap'
                  }}
                  className="open-os-badge-ok"
                >
                  ✓ Endereço corrigido
                </span>
              )}
              {osType !== 'estoque' && !(isContractMode && hasMultipleSites) && (
                <button
                  onClick={() => {
                    if (!originalAddress) {
                      setOriginalAddress(selectedEquipment.Endereco);
                    }
                    setIsEditingAddress(true);
                    setAddressSearch('');
                    setAddressSuggestions([]);
                    setCorrectedAddress(null);
                  }}
                  className="open-os-btn-outline-accent"
                  style={{
                    fontSize: '11px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    fontWeight: 500,
                    color: '#7c4dff',
                    background: 'transparent',
                    border: '1px solid #7c4dff',
                    borderRadius: '6px',
                    padding: '4px 10px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    height: 'fit-content',
                    lineHeight: '1.4',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#7c4dff';
                    e.target.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = '#7c4dff';
                  }}
                >
                  Endereço incorreto
                </button>
              )}
            </div>
          ) : (
            <div>
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <input
                  ref={addressInputRef}
                  type="text"
                  value={addressSearch}
                  onChange={(e) => setAddressSearch(e.target.value)}
                  placeholder="Pesquise o endereço correto aqui"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c4dff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)';
                  }}
                />
                {isLoadingAddress && (
                  <div style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '12px',
                    color: 'var(--text-muted, #718096)'
                  }}>
                    Buscando...
                  </div>
                )}
              </div>
              {addressSuggestions.length > 0 && (
                <div style={{
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  borderRadius: '8px',
                  background: 'var(--modal-bg, #ffffff)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '4px'
                }}>
                  {addressSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      className="open-os-list-item"
                      onClick={() => handleAddressSelect(suggestion)}
                      style={{
                        padding: '10px 12px',
                        borderBottom: index < addressSuggestions.length - 1 ? '1px solid var(--border-subtle, #f0f0f0)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-body, #2d3748)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      {suggestion.display}
                    </div>
                  ))}
                </div>
              )}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px'
              }}>
                <button
                  onClick={() => {
                    if (originalAddress === selectedEquipment.Endereco) {
                      setOriginalAddress(null);
                    }
                    setIsEditingAddress(false);
                    setAddressSearch('');
                    setAddressSuggestions([]);
                    setCorrectedAddress(null);
                  }}
                  style={{
                    fontSize: '12px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    fontWeight: 500,
                    color: 'var(--text-muted, #718096)',
                    background: 'transparent',
                    border: '1px solid var(--border-subtle, #e2e8f0)',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Separador com botão para expandir/colapsar último atendimento */}
      {!isContractMode && (
        <div style={{
          position: 'relative',
          height: '1px',
          background: 'var(--border-subtle, #e8e8e8)',
          margin: '24px 0 16px 0'
        }}>
          <button
            className="open-os-last-attendance-btn"
            onClick={() => setLastOsSectionState(!lastOsSectionState)}
            style={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'var(--modal-bg, #ffffff)',
              border: '1px solid var(--border-subtle, #e8e8e8)',
              borderRadius: '4px',
              padding: '4px 10px',
              fontSize: '11px',
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              fontWeight: 500,
              color: 'var(--text-muted, #718096)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#7c4dff';
              e.target.style.color = '#7c4dff';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = 'var(--border-subtle, #e8e8e8)';
              e.target.style.color = 'var(--text-muted, #718096)';
            }}
          >
            <i className={`bi ${lastOsSectionState ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: '10px' }}></i>
            ÚLTIMO ATENDIMENTO
            {lastOsDate && (() => {
              const date = new Date(lastOsDate);
              const today = new Date();
              date.setHours(0, 0, 0, 0);
              today.setHours(0, 0, 0, 0);
              const diffTime = Math.abs(today - date);
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              const isRecent = diffDays <= 30;

              const day = String(date.getDate()).padStart(2, '0');
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const year = date.getFullYear();
              const formatted = `${day}/${month}/${year}`;

              return (
                <span
                  style={{
                    marginLeft: '8px',
                    fontWeight: 500,
                    color: isRecent ? '#e53e3e' : 'var(--text-body, #2d3748)',
                    animation: isRecent ? 'pulse 2s infinite' : 'none'
                  }}
                  title={
                    isRecent
                      ? 'O último atendimento foi realizado dentro dos últimos 30 dias.'
                      : undefined
                  }
                >
                  {formatted}
                </span>
              );
            })()}
          </button>
        </div>
      )}

      {/* Último Atendimento */}
      {!isContractMode && lastOsSectionState && (
        <div>
          {isLoadingLastOs ? (
            <div style={{
              fontSize: '13px',
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-muted, #718096)',
              fontStyle: 'italic'
            }}>
              Carregando...
            </div>
          ) : lastOsDate ? (
            <div>
              {/* Primeira linha: DATA, OS, TIPO, SOLICITANTE */}
              <div style={{
                display: 'flex',
                gap: '16px',
                marginBottom: '12px',
                flexWrap: 'wrap',
                alignItems: 'flex-start'
              }}>
                {/* DATA */}
                <div style={{ flex: '0 0 auto' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '4px',
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    DATA
                  </label>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-body, #2d3748)'
                  }}>
                    {formatDateToPTBR(lastOsDate)}
                  </div>
                </div>

                {/* OS */}
                {lastOsData && lastOsData.codigo && (
                  <div style={{ flex: '0 0 auto' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      OS
                    </label>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)'
                    }}>
                      {lastOsData.codigo}
                    </div>
                  </div>
                )}

                {/* TIPO */}
                {lastOsData && lastOsData.preventiva && (
                  <div style={{ flex: '0 0 auto' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      TIPO
                    </label>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)'
                    }}>
                      {translatePreventivaType(lastOsData.preventiva)}
                    </div>
                  </div>
                )}

                {/* SOLICITANTE */}
                {lastOsData && lastOsData.solicitante && (
                  <div style={{ flex: '0 0 auto' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      SOLICITANTE
                    </label>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)'
                    }}>
                      {lastOsData.solicitante}
                    </div>
                  </div>
                )}

                {/* Botão LAUDO */}
                {lastOsData && lastOsData.codigo && (
                  <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-end' }}>
                    <button
                      onClick={() => setShowLaudo(!showLaudo)}
                      style={{
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: '#7c4dff',
                        background: 'transparent',
                        border: '1px solid #7c4dff',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#7c4dff';
                        e.target.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'transparent';
                        e.target.style.color = '#7c4dff';
                      }}
                    >
                      LAUDO
                    </button>
                  </div>
                )}
              </div>

              {/* Segunda linha: MOTIVO e TÉCNICO */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '12px',
                flexWrap: 'nowrap',
                alignItems: 'flex-start'
              }}>
                {/* MOTIVO */}
                {lastOsData && lastOsData.nome && (
                  <div style={{ flex: '0 1 auto', minWidth: '200px', maxWidth: '500px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      MOTIVO
                    </label>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)'
                    }}>
                      {lastOsData.nome}
                      {(lastOsData.defeito || lastOsData.obsDefeito || lastOsData.solucaoDefeito) && (
                        <button
                          onClick={() => setDefeitoDetailsState(!defeitoDetailsState)}
                          style={{
                            fontSize: '11px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            fontWeight: 500,
                            color: '#7c4dff',
                            background: 'transparent',
                            border: '1px solid #7c4dff',
                            borderRadius: '6px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap',
                            marginLeft: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#7c4dff';
                            e.target.style.color = '#ffffff';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = '#7c4dff';
                          }}
                        >
                          {defeitoDetailsState ? 'Ocultar defeito' : 'Ver defeito'}
                        </button>
                      )}
                    </div>
                    
                    {/* Seção expandível dos detalhes do defeito */}
                    {defeitoDetailsState && (lastOsData.defeito || lastOsData.obsDefeito || lastOsData.solucaoDefeito) && (
                      <div style={{
                        marginTop: '12px',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '8px',
                        background: 'var(--bg-surface-alt, #f7fafc)',
                        padding: '12px'
                      }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '12px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif"
                        }}>
                          <thead>
                            <tr>
                              <th style={{
                                textAlign: 'left',
                                padding: '8px',
                                borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                                fontWeight: 600,
                                color: 'var(--text-body, #2d3748)',
                                fontSize: '11px',
                                textTransform: 'uppercase'
                              }}>
                                Defeito
                              </th>
                              <th style={{
                                textAlign: 'left',
                                padding: '8px',
                                borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                                fontWeight: 600,
                                color: 'var(--text-body, #2d3748)',
                                fontSize: '11px',
                                textTransform: 'uppercase'
                              }}>
                                Observação
                              </th>
                              <th style={{
                                textAlign: 'left',
                                padding: '8px',
                                borderBottom: '1px solid var(--border-subtle, #e2e8f0)',
                                fontWeight: 600,
                                color: 'var(--text-body, #2d3748)',
                                fontSize: '11px',
                                textTransform: 'uppercase'
                              }}>
                                Solução
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{
                                padding: '8px',
                                borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                                color: 'var(--text-body, #2d3748)'
                              }}>
                                {lastOsData.defeito || 'N/A'}
                              </td>
                              <td style={{
                                padding: '8px',
                                borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                                color: 'var(--text-body, #2d3748)'
                              }}>
                                {lastOsData.obsDefeito || 'N/A'}
                              </td>
                              <td style={{
                                padding: '8px',
                                borderBottom: '1px solid var(--border-subtle, #f0f0f0)',
                                color: 'var(--text-body, #2d3748)'
                              }}>
                                {lastOsData.solucaoDefeito || 'N/A'}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* TÉCNICO */}
                {lastOsData && lastOsData.tecnico && (
                  <div style={{ flex: '0 0 auto', minWidth: '150px' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontSize: '11px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-muted, #718096)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      TÉCNICO
                    </label>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: 500,
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      color: 'var(--text-body, #2d3748)'
                    }}>
                      {lastOsData.tecnico}
                    </div>
                  </div>
                )}
              </div>

              {/* Aviso de 30 dias */}
              {(() => {
                const date = new Date(lastOsDate);
                const today = new Date();
                date.setHours(0, 0, 0, 0);
                today.setHours(0, 0, 0, 0);
                const diffTime = Math.abs(today - date);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                return diffDays < 30 ? (
                  <div
                    className="open-os-badge-expired"
                    style={{
                    fontSize: '11px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: '#c53030',
                    fontWeight: 600,
                    marginTop: '4px',
                    marginBottom: '8px',
                    padding: '4px 8px',
                    background: '#fff5f5',
                    border: '1px solid #feb2b2',
                            borderRadius: '4px',
                            display: 'inline-block'
                          }}>
                            ⚠️ Atenção! Último atendimento realizado dentro de 30 dias.
                          </div>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Último Atendimento
                      </label>
                      <div style={{
                        fontSize: '13px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        fontStyle: 'italic'
                      }}>
                        Primeiro atendimento registrado
                      </div>
                    </div>
                  )}
                </div>
              )}

          {/* Separador - só exibe quando a seção de Último Atendimento estiver expandida */}
          {!isContractMode && lastOsSectionState && (
            <div style={{
              height: '1px',
              background: 'var(--border-subtle, #e8e8e8)',
              margin: '0 0 16px 0'
            }}></div>
          )}

          {/* SOLICITANTE, CONTATO, EMAIL e DEFEITO */}
          {solicitante !== undefined && (
            <div style={{
              display: 'flex',
              gap: '20px',
              marginTop: lastOsSectionState ? '0' : '24px',
              marginBottom: '12px',
              flexWrap: 'wrap'
            }}>
              {/* SOLICITANTE */}
              <div style={{ flex: '0 0 auto', maxWidth: 'calc(50% - 10px)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: 'var(--text-muted, #718096)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  SOLICITANTE <span style={{ color: '#c53030' }}>*</span>
                  <TB02112IconButton fieldKey="solicitante" disabledTitle="Preencher solicitante do equipamento" />
                </label>
                <input
                  ref={solicitanteInputRef}
                  type="text"
                  value={solicitante || ''}
                  onChange={(e) => setSolicitante && setSolicitante(e.target.value)}
                  placeholder="Nome e sobrenome"
                  maxLength={30}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    border: !solicitante?.trim() ? '1px solid #c53030' : '1px solid var(--border-subtle, #e2e8f0)',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c4dff';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = !solicitante?.trim() ? '#c53030' : 'var(--border-subtle, #e2e8f0)';
                  }}
                />
              </div>

              {/* CONTATO (Fone1 obrigatório; WhatsApp e Fone2 opcionais) */}
              <div style={{ flex: '0 0 auto', maxWidth: 'calc(26.25% - 10px)' }} ref={contatoDropdownRef}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: 'var(--text-muted, #718096)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  CONTATO <span style={{ color: '#c53030' }}>*</span>
                  <TB02112IconButton fieldKey="contato" disabledTitle="Preencher contato do equipamento" />
                </label>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input
                    type="text"
                    value={
                      contatoType === 'fone1' ? (contatoFone1 || '') :
                      contatoType === 'whatsapp' ? (contatoWhatsApp || '') :
                      (contatoFone2 || '')
                    }
                    onChange={(e) => {
                      const maskedValue = applyPhoneMask(e.target.value);
                      if (contatoType === 'fone1') setContatoFone1 && setContatoFone1(maskedValue);
                      else if (contatoType === 'whatsapp') setContatoWhatsApp && setContatoWhatsApp(maskedValue);
                      else setContatoFone2 && setContatoFone2(maskedValue);
                    }}
                    placeholder="(XX)XXXXX-XXXX"
                    maxLength={14}
                    style={{
                      width: '100%',
                      padding: '8px 100px 8px 12px',
                      fontSize: '13px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      border: !(contatoFone1 || '').trim() ? '1px solid #c53030' : '1px solid var(--border-subtle, #e2e8f0)',
                      borderRadius: '6px',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#7c4dff';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = !(contatoFone1 || '').trim() ? '#c53030' : 'var(--border-subtle, #e2e8f0)';
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '8px',
                    pointerEvents: 'none'
                  }}>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); setContatoDropdownOpen((v) => !v); }}
                      onMouseDown={(e) => e.preventDefault()}
                      style={{
                        pointerEvents: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: 'rgba(203, 184, 255, 0.08)',
                        border: '1px solid rgba(124, 77, 255, 0.25)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--accent, #5a67d8)',
                        transition: 'all 0.2s ease'
                      }}
                      className="open-os-fone-trigger"
                    >
                      {contatoType === 'fone1' ? 'Fone1' : contatoType === 'whatsapp' ? 'WhatsApp' : 'Fone2'}
                      <i className="bi bi-chevron-down" style={{ fontSize: '10px', opacity: contatoDropdownOpen ? 0.7 : 1 }}></i>
                    </button>
                  </div>
                  {contatoDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: '4px',
                      minWidth: '140px',
                      background: 'var(--modal-bg, #ffffff)',
                      border: '1px solid var(--border-subtle, #e2e8f0)',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 50,
                      overflow: 'hidden'
                    }}>
                      {[
                        { key: 'fone1', label: 'Fone1', value: contatoFone1 },
                        { key: 'whatsapp', label: 'WhatsApp', value: contatoWhatsApp },
                        { key: 'fone2', label: 'Fone2', value: contatoFone2 }
                      ].map(({ key, label, value }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => { setContatoType(key); setContatoDropdownOpen(false); }}
                          className="open-os-fone-menu-item"
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            padding: '10px 12px',
                            border: 'none',
                            background: contatoType === key ? 'rgba(203, 184, 255, 0.08)' : 'transparent',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            color: 'var(--text-body, #2d3748)',
                            textAlign: 'left',
                            transition: 'background 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (contatoType !== key) e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                          }}
                          onMouseLeave={(e) => {
                            if (contatoType !== key) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <span>{label}</span>
                          {value && String(value).trim() !== '' && (
                            <i className="bi bi-check-circle-fill" style={{ fontSize: '14px', color: '#22c55e' }}></i>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* EMAIL */}
              <div style={{ flex: '0 0 auto', maxWidth: 'calc(25% - 10px)' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                  fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                  color: 'var(--text-muted, #718096)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  EMAIL
                  <TB02112IconButton fieldKey="email" disabledTitle="Preencher email do equipamento" />
                </label>
                <input
                  type="email"
                  value={email || ''}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={(e) => {
                    e.target.style.borderColor = emailError ? '#c53030' : 'var(--border-subtle, #e2e8f0)';
                    if (e.target.value) {
                      handleEmailChange(e.target.value);
                    }
                  }}
                  placeholder="email@exemplo.com"
                  style={{
                    width: '125%',
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    border: emailError ? '1px solid #c53030' : '1px solid var(--border-subtle, #e2e8f0)',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#7c4dff';
                  }}
                />
                {emailError && (
                  <div style={{
                    fontSize: '11px',
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: '#c53030',
                    marginTop: '4px'
                  }}>
                    {emailError}
                  </div>
                )}
              </div>

              {/* DEFEITO */}
              <div style={{ flex: '0 0 100%', maxWidth: '100%' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '4px'
                }}>
                  <label style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                    color: 'var(--text-muted, #718096)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    margin: 0
                  }}                  >
                    DEFEITO <span style={{ color: '#c53030' }}>*</span>
                  </label>
                  {defeitoVinculoEquipAtivo && !codEquipModelo ? (
                    <span
                      style={{
                        fontSize: '10px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#c05621',
                        fontWeight: 600,
                      }}
                    >
                      Modelo do equipamento necessário para listar defeitos vinculados.
                    </span>
                  ) : null}
                  {selectedDefeito && selectedDefeito.solucao && (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} data-solucao-tooltip>
                      <i
                        className="bi bi-lightbulb"
                        style={{
                          fontSize: '11px',
                          color: '#7c4dff',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          lineHeight: '1',
                          display: 'inline-block'
                        }}
                        onClick={() => setShowSolucaoTooltip && setShowSolucaoTooltip(!showSolucaoTooltip)}
                        onMouseEnter={(e) => {
                          e.target.style.color = '#5a3dc4';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.color = '#7c4dff';
                        }}
                      ></i>
                      {showSolucaoTooltip && (
                        <div
                          data-solucao-tooltip
                          style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: 0,
                            marginBottom: '6px',
                            padding: '10px 14px',
                            background: 'var(--text-body, #2d3748)',
                            color: '#ffffff',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            borderRadius: '6px',
                            zIndex: 1000,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            width: '500px',
                            maxWidth: '500px',
                            whiteSpace: 'normal',
                            lineHeight: '1.5'
                          }}
                        >
                          {selectedDefeito.solucao}
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '20px',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid #2d3748'
                          }}></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ display: selectedDefeito ? 'flex' : 'block', gap: selectedDefeito ? '12px' : '0', alignItems: 'flex-start' }}>
                  <div style={{ position: 'relative', width: selectedDefeito ? '40%' : '100%' }}>
                    <input
                      type="text"
                      value={selectedDefeito ? selectedDefeito.nome : (defeitoSearch || '')}
                      onChange={(e) => {
                        if (selectedDefeito) {
                          handleDefeitoClear();
                        }
                        setDefeitoSearch && setDefeitoSearch(e.target.value);
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#7c4dff';
                      }}
                      placeholder="Buscar defeito..."
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        paddingRight: isLoadingDefeitos ? '32px' : '12px',
                        fontSize: '13px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)';
                      }}
                    />
                    {isLoadingDefeitos && (
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '16px',
                        height: '16px',
                        border: '2px solid #7c4dff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }}></div>
                    )}
                    {defeitoSuggestions.length > 0 && !selectedDefeito && defeitoSearch && defeitoSearch.trim().length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        background: 'var(--modal-bg, #ffffff)',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}>
                        {defeitoSuggestions.map((defeito, index) => (
                          <div
                            key={defeito.codigo}
                            className="open-os-list-item"
                            onClick={() => handleDefeitoSelect(defeito)}
                            style={{
                              padding: '10px 12px',
                              borderBottom: index < defeitoSuggestions.length - 1 ? '1px solid var(--border-subtle, #f0f0f0)' : 'none',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              fontSize: '13px',
                              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                              color: 'var(--text-body, #2d3748)'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'transparent';
                            }}
                          >
                            {defeito.nome}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Campo de observações do defeito */}
                  {selectedDefeito && (
                    <div className="open-os-obs-voice-wrap" style={{ flex: '0 0 60%', position: 'relative' }}>
                      {/* Contador de caracteres - posicionado absolutamente acima do textarea */}
                      <div style={{
                        position: 'absolute',
                        top: '-18px',
                        right: 0,
                        zIndex: 1
                      }}>
                        <span style={{
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          color: (defeitoObservacao?.length || 0) >= 15 ? '#48bb78' : '#c53030',
                          fontWeight: 500,
                          transition: 'color 0.2s ease',
                          opacity: 0.6
                        }}>
                          {defeitoObservacao?.length || 0}/15
                        </span>
                      </div>
                      <textarea
                        ref={defeitoObservacaoRef}
                        value={defeitoObservacao || ''}
                        onChange={(e) => setDefeitoObservacao && setDefeitoObservacao(e.target.value)}
                        placeholder="Adicione observações aqui... (obrigatório)"
                        style={{
                          width: '100%',
                          padding: '8px 62px 8px 12px',
                          fontSize: '13px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          border: selectedDefeito && !defeitoObservacao?.trim() ? '1px solid #c53030' : '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          outline: 'none',
                          transition: 'all 0.2s ease',
                          resize: 'both',
                          minHeight: '38px',
                          height: '38px',
                          lineHeight: '1.5',
                          overflow: 'auto'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#7c4dff';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = selectedDefeito && !defeitoObservacao?.trim() ? '#c53030' : 'var(--border-subtle, #e2e8f0)';
                        }}
                      />
                      <div className="open-os-obs-voice-tools">
                        <BoardChatAiWriteMenu
                          open={obsAiWriteOpen}
                          onOpenChange={(next) => {
                            hideObsTip();
                            if (next) {
                              setObsVoiceError('');
                              obsVoice.cancel();
                            }
                            setObsAiWriteOpen(next);
                          }}
                          tipProps={obsTipProps}
                          hideTip={hideObsTip}
                          draft={defeitoObservacao || ''}
                          applyLabel="Usar nas observações"
                          onApply={(text) => {
                            const next = String(text || '').trim();
                            if (next && typeof setDefeitoObservacao === 'function') {
                              setDefeitoObservacao(next);
                            }
                            setObsAiWriteOpen(false);
                          }}
                        />
                        <button
                          type="button"
                          className={`open-os-obs-voice-mic${obsVoice.recording || obsVoice.busy ? ' is-recording' : ''}`}
                          aria-label={obsVoice.recording ? 'Gravando áudio' : 'Fala para texto'}
                          aria-pressed={obsVoice.recording}
                          disabled={obsVoice.busy}
                          {...obsTipProps(obsVoice.recording ? 'Gravando…' : 'Fala para texto', 'above')}
                          onClick={() => {
                            if (obsVoice.busy) return;
                            hideObsTip();
                            setObsVoiceError('');
                            setObsAiWriteOpen(false);
                            obsVoice.toggle();
                          }}
                        >
                          <Mic size={14} strokeWidth={2} />
                        </button>
                      </div>
                      <BchTipLayer tip={obsTip} />
                      {obsVoice.recording || obsVoice.busy ? (
                        <div className="open-os-obs-voice-pop" role="status" aria-live="polite">
                          <span className={`open-os-obs-voice-pulse${obsVoice.busy ? ' is-busy' : ''}`} aria-hidden />
                          <span className="open-os-obs-voice-timer">
                            {`${Math.floor((obsVoice.elapsedSec || 0) / 60)}:${String((obsVoice.elapsedSec || 0) % 60).padStart(2, '0')}`}
                          </span>
                          <span className="open-os-obs-voice-label">
                            {obsVoice.busy ? 'Processando…' : 'Gravando…'}
                          </span>
                          <button
                            type="button"
                            className="open-os-obs-voice-transcribe"
                            disabled={obsVoice.busy}
                            onClick={handleObsTranscribe}
                          >
                            <Type size={13} strokeWidth={1.9} />
                            Transcrever
                          </button>
                          <button
                            type="button"
                            className="open-os-obs-voice-cancel"
                            disabled={obsVoice.busy}
                            aria-label="Cancelar gravação"
                            onClick={() => obsVoice.cancel()}
                          >
                            <X size={13} strokeWidth={2} />
                          </button>
                        </div>
                      ) : null}
                      {obsVoiceError ? (
                        <div className="open-os-obs-voice-error">{obsVoiceError}</div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Linha de separação após DEFEITO */}
          {solicitante !== undefined && (
            <div
              style={{
                height: '1px',
                background: 'var(--border-subtle, #e8e8e8)',
                margin: '0 0 16px 0'
              }}
            ></div>
          )}

          {/* Ações finais da etapa: Informar contadores / Programar atendimento / Gerar OS */}
          {showCounters !== undefined && (
            <div
              style={{
                display: 'flex',
                justifyContent: showCounters && !countersConfirmed ? 'space-between' : 'flex-end',
                gap: '8px',
                marginTop: '8px',
                alignItems: 'center',
                flexWrap: 'wrap'
              }}
            >
              {/* Container de contadores (visível após clicar em Informar contadores, mas desaparece quando confirmado) */}
              {!isContractMode && showCounters && !countersConfirmed && (
                <div style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  padding: '16px',
                  border: '1px solid var(--border-subtle, #e2e8f0)',
                  borderRadius: '8px',
                  background: 'var(--bg-surface-alt, #fafbfc)',
                  position: 'relative'
                }}>
                  {/* Linha de campos de contadores */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    alignItems: 'flex-start',
                    marginBottom: '7px'
                  }}>
                    {/* Contador P&B */}
                    <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Contador P&B
                      </label>
                      {loadingCounters ? (
                        <div style={{
                          width: '100%',
                          height: '36px',
                          padding: '8px 10px',
                          fontSize: '13px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #7c4dff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite'
                          }}></div>
                          <span style={{ color: 'var(--text-muted, #718096)', fontSize: '12px' }}>Carregando...</span>
                        </div>
                      ) : (
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithMask(counterValues?.pb || '')}
                          placeholder={lastCounters?.pb > 0 ? formatNumberWithMask(lastCounters.pb) : ''}
                          onChange={(e) => handleCounterChange('pb', e.target.value)}
                          onBlur={() => handleCounterBlur('pb')}
                          disabled={countersConfirmed}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            fontSize: '13px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            border: counterErrors?.pb && !countersConfirmed
                              ? '1px solid #c53030'
                              : '1px solid var(--border-subtle, #e2e8f0)',
                            borderRadius: '6px',
                            outline: 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            if (!countersConfirmed) {
                              e.target.style.borderColor = '#7c4dff';
                            }
                          }}
                        />
                      )}
                      <div style={{
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#c53030',
                        marginTop: '4px',
                        minHeight: '18px',
                        lineHeight: '18px'
                      }}>
                        {counterErrors?.pb && !countersConfirmed ? counterErrors.pb : '\u00A0'}
                      </div>
                    </div>

                    {/* Contador Cor */}
                    <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Contador Cor
                      </label>
                      {loadingCounters ? (
                        <div style={{
                          width: '100%',
                          height: '36px',
                          padding: '8px 10px',
                          fontSize: '13px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #7c4dff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite'
                          }}></div>
                          <span style={{ color: 'var(--text-muted, #718096)', fontSize: '12px' }}>Carregando...</span>
                        </div>
                      ) : (
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithMask(counterValues?.color || '')}
                          placeholder={lastCounters?.color > 0 ? formatNumberWithMask(lastCounters.color) : ''}
                          onChange={(e) => handleCounterChange('color', e.target.value)}
                          onBlur={() => handleCounterBlur('color')}
                          disabled={countersConfirmed}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            fontSize: '13px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            border: counterErrors?.color && !countersConfirmed
                              ? '1px solid #c53030'
                              : '1px solid var(--border-subtle, #e2e8f0)',
                            borderRadius: '6px',
                            outline: 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            if (!countersConfirmed) {
                              e.target.style.borderColor = '#7c4dff';
                            }
                          }}
                        />
                      )}
                      <div style={{
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#c53030',
                        marginTop: '4px',
                        minHeight: '18px',
                        lineHeight: '18px'
                      }}>
                        {counterErrors?.color && !countersConfirmed ? counterErrors.color : '\u00A0'}
                      </div>
                    </div>

                    {/* Contador Total */}
                    <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column' }}>
                      <label style={{
                        display: 'block',
                        marginBottom: '4px',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: 'var(--text-muted, #718096)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Contador Total
                      </label>
                      {loadingCounters ? (
                        <div style={{
                          width: '100%',
                          height: '36px',
                          padding: '8px 10px',
                          fontSize: '13px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          background: 'var(--bg-surface-alt, #f7fafc)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            width: '14px',
                            height: '14px',
                            border: '2px solid #7c4dff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite'
                          }}></div>
                          <span style={{ color: 'var(--text-muted, #718096)', fontSize: '12px' }}>Carregando...</span>
                        </div>
                      ) : (
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberWithMask(counterValues?.total || '')}
                          placeholder={lastCounters?.total > 0 ? formatNumberWithMask(lastCounters.total) : ''}
                          onChange={(e) => handleCounterChange('total', e.target.value)}
                          onBlur={() => handleCounterBlur('total')}
                          disabled={countersConfirmed}
                          style={{
                            width: '100%',
                            padding: '8px 10px',
                            fontSize: '13px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            border: counterErrors?.total && !countersConfirmed
                              ? '1px solid #c53030'
                              : '1px solid var(--border-subtle, #e2e8f0)',
                            borderRadius: '6px',
                            outline: 'none',
                            transition: 'all 0.2s ease'
                          }}
                          onFocus={(e) => {
                            if (!countersConfirmed) {
                              e.target.style.borderColor = '#7c4dff';
                            }
                          }}
                        />
                      )}
                      <div style={{
                        fontSize: '11px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        color: '#c53030',
                        marginTop: '4px',
                        minHeight: '18px',
                        lineHeight: '18px'
                      }}>
                        {counterErrors?.total && !countersConfirmed ? counterErrors.total : '\u00A0'}
                      </div>
                    </div>
                  </div>

                  {/* Botões de ação dentro do container (Confirmar/Cancelar contadores) */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      type="button"
                      className="open-os-btn-secondary"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 500,
                        color: 'var(--text-muted, #4a5568)',
                        background: 'var(--bg-surface-alt, #f7fafc)',
                        border: '1px solid var(--border-subtle, #e2e8f0)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: countersConfirmed ? 0.85 : 1
                      }}
                      disabled={loadingCounters}
                      onClick={handleConfirmCounters}
                      onMouseEnter={(e) => {
                        if (!countersConfirmed) {
                          e.target.style.background = 'var(--border-subtle, #edf2f7)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!countersConfirmed) {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }
                      }}
                    >
                      <i className="bi bi-printer" style={{ fontSize: '14px', lineHeight: 1 }}></i>
                      <span>Confirmar contadores</span>
                    </button>

                    {/* Botão Cancelar edição de contadores */}
                    {!countersConfirmed && (
                      <button
                        type="button"
                        className="open-os-btn-ghost"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '8px 10px',
                          fontSize: '12px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: 500,
                          color: 'var(--text-muted, #4a5568)',
                          background: 'transparent',
                          border: '1px solid var(--border-subtle, #e2e8f0)',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => {
                          if (setShowCounters) setShowCounters(false);
                          if (setCountersConfirmed) setCountersConfirmed(false);
                          if (setCounterValues) setCounterValues({ pb: '', color: '', total: '' });
                          if (setCounterErrors) setCounterErrors({ pb: '', color: '', total: '' });
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'transparent';
                        }}
                      >
                        <span>Cancelar</span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Botões de ação (Informar contadores + Contadores confirmados + Programar atendimento + Gerar OS) */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
                marginLeft: 'auto'
              }}>
                {/* Botão Informar contadores */}
                {!isContractMode && validateRequiredFields && !showCounters && (
                  <button
                    type="button"
                    className="open-os-btn-secondary"
                    disabled={!validateRequiredFields() || loadingCounters}
                    onClick={() => {
                      if (validateRequiredFields()) {
                        handleOpenCounters();
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (validateRequiredFields() && !loadingCounters) {
                        e.target.style.background = 'var(--border-subtle, #edf2f7)';
                      } else if (!validateRequiredFields()) {
                        e.target.style.cursor = 'not-allowed';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (validateRequiredFields() && !loadingCounters) {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      } else if (!validateRequiredFields()) {
                        e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                        e.target.style.cursor = 'not-allowed';
                      }
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      width: '154px',
                      height: '28px',
                      padding: '0 10px',
                      fontSize: '11px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      color: validateRequiredFields() ? 'var(--text-muted, #4a5568)' : 'var(--text-muted, #9ca3af)',
                      background: validateRequiredFields() ? 'var(--bg-surface-alt, #f7fafc)' : 'var(--bg-surface-alt, #e5e7eb)',
                      border: validateRequiredFields() ? '1px solid var(--border-subtle, #e2e8f0)' : '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: validateRequiredFields() && !loadingCounters ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease',
                      opacity: validateRequiredFields() ? 1 : 0.6
                    }}
                  >
                    <i className="bi bi-printer" style={{ fontSize: '12px', lineHeight: 1 }}></i>
                    <span>Informar contadores</span>
                  </button>
                )}

                {/* Botão Contadores confirmados (aparece ao lado do Gerar OS quando confirmado) */}
                {!isContractMode && countersConfirmed && showCounters && (
                  <button
                    type="button"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      fontSize: '11px',
                      fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                      fontWeight: 500,
                      color: 'var(--text-muted, #4a5568)',
                      background: 'transparent',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'default',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <i className="bi bi-check-circle-fill" style={{ fontSize: '12px', lineHeight: 1, color: 'var(--text-muted, #4a5568)', background: 'transparent' }}></i>
                    <span style={{ background: 'transparent' }}>Contadores confirmados</span>
                  </button>
                )}

                {/* Botões de ação (Programar atendimento + Gerar OS) */}
                {validateRequiredFields && (
                  <>
                    {/* Botão Tipo de OS (somente modo único: serial/patrimônio) */}
                    {!isContractMode && osType !== 'estoque' && (
                      <TipoOSOptionsButton
                        disabled={!baseFormValid}
                        selectedValue={tipoOsValue}
                        confirmed={tipoOsUniqueConfirmed}
                        allowedValues={allowedTiposParaDefeito}
                        needsAttention={tipoOsNeedsAttention}
                        onChange={(value) => {
                          if (setTipoOsUniqueValue) setTipoOsUniqueValue(value);
                          if (setTipoOsUniqueConfirmed) setTipoOsUniqueConfirmed(true);
                        }}
                      />
                    )}

                    {/* Botão Programar atendimento */}
                    <div style={{ position: 'relative' }} data-schedule-dropdown>
                      <button
                        type="button"
                        className="open-os-btn-action"
                        disabled={!validateRequiredFields()}
                        onClick={() => {
                          if (!isScheduled && validateRequiredFields()) {
                            // Minimizar "ÚLTIMO ATENDIMENTO" quando abrir dropdown de programação
                            if (setShowLastOsSection) setShowLastOsSection(false);
                            setShowScheduleDropdown && setShowScheduleDropdown(!showScheduleDropdown);
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isScheduled && validateRequiredFields()) {
                            e.target.style.background = '#4b5563';
                            e.target.style.borderColor = 'rgba(213, 198, 255, 1)';
                          } else if (!validateRequiredFields()) {
                            e.target.style.cursor = 'not-allowed';
                          } else {
                            setShowScheduleTooltip && setShowScheduleTooltip(true);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isScheduled && validateRequiredFields()) {
                            e.target.style.background = '#6b7280';
                            e.target.style.borderColor = 'rgba(213, 198, 255, 1)';
                          } else if (!isScheduled && !validateRequiredFields()) {
                            e.target.style.background = 'var(--bg-surface-alt, #e5e7eb)';
                            e.target.style.borderColor = 'var(--border-subtle, #d1d5db)';
                          } else {
                            setShowScheduleTooltip && setShowScheduleTooltip(false);
                          }
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          padding: '6px 10px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          fontWeight: isScheduled ? 500 : 600,
                          color: isScheduled ? 'var(--text-muted, #4a5568)' : (validateRequiredFields() ? '#ffffff' : 'var(--text-muted, #9ca3af)'),
                          background: isScheduled ? 'transparent' : (validateRequiredFields() ? '#6b7280' : 'var(--bg-surface-alt, #e5e7eb)'),
                          border: isScheduled ? 'none' : (validateRequiredFields() ? '1px solid rgba(213, 198, 255, 1)' : '1px solid var(--border-subtle, #d1d5db)'),
                          borderRadius: '6px',
                          cursor: isScheduled ? 'default' : (validateRequiredFields() ? 'pointer' : 'not-allowed'),
                          transition: 'all 0.2s ease',
                          boxShadow: isScheduled ? 'none' : (validateRequiredFields() ? '0 2px 6px rgba(124, 77, 255, 0.35)' : 'none'),
                          position: 'relative',
                          opacity: validateRequiredFields() || isScheduled ? 1 : 0.6
                        }}
                      >
                        {isScheduled ? (
                          <>
                            <i className="bi bi-check-circle-fill" style={{ fontSize: '12px', lineHeight: 1, color: 'var(--text-muted, #4a5568)', background: 'transparent' }}></i>
                            <span style={{ background: 'transparent' }}>Atendimento programado</span>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-calendar-check" style={{ fontSize: '12px', lineHeight: 1 }}></i>
                            <span>Programar atendimento</span>
                          </>
                        )}
                      </button>
                      {isScheduled && scheduleData && showScheduleTooltip && (
                        <div style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          marginBottom: '8px',
                          padding: '8px 12px',
                          background: '#1f2937',
                          color: '#ffffff',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                          whiteSpace: 'nowrap',
                          zIndex: 10001,
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          pointerEvents: 'none'
                        }}>
                          <div style={{ marginBottom: '4px', fontWeight: 500 }}>
                            {scheduleData.type === 'apenas_tecnico'
                              ? 'Sem programação'
                              : scheduleData.type === 'today'
                              ? 'Programado para hoje'
                              : scheduleData.type === 'tomorrow'
                              ? 'Programado para amanhã'
                              : scheduleData.date ? `Programado para ${new Date(scheduleData.date + 'T00:00:00').toLocaleDateString('pt-BR')}` : 'Programado'}
                          </div>
                          <div style={{ fontSize: '10px', color: 'var(--border-subtle, #d1d5db)' }}>
                            {scheduleData.technician?.nome || ''}
                          </div>
                        </div>
                      )}

                      {/* Dropdown de programação */}
                      {showScheduleDropdown && !isScheduled && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          right: 0,
                          marginTop: '4px',
                          background: 'var(--modal-bg, #ffffff)',
                          border: '1px solid rgba(213, 198, 255, 1)',
                          borderRadius: '6px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 1000,
                          minWidth: '200px',
                          overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}>
                          <div style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'var(--text-body, #2d3748)',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif"
                          }}
                          className="open-os-list-item"
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onClick={() => {
                            // Minimizar "ÚLTIMO ATENDIMENTO" quando programar atendimento
                            if (setShowLastOsSection) setShowLastOsSection(false);
                            setScheduleType && setScheduleType('today');
                            const todayStr = getTodayLocal();
                            setSelectedScheduleDate && setSelectedScheduleDate(todayStr);
                            setShowScheduleDropdown && setShowScheduleDropdown(false);
                            setShowScheduleSidebar && setShowScheduleSidebar(true);
                            handleLoadScheduleData && handleLoadScheduleData();
                          }}>
                            <i className="bi bi-calendar-day" style={{ fontSize: '14px', color: '#7c4dff' }}></i>
                            <span>Programar para hoje</span>
                          </div>
                          <div style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'var(--text-body, #2d3748)',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            borderTop: '1px solid var(--border-subtle, #e2e8f0)'
                          }}
                          className="open-os-list-item"
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onClick={() => {
                            // Minimizar "ÚLTIMO ATENDIMENTO" quando programar atendimento
                            if (setShowLastOsSection) setShowLastOsSection(false);
                            setScheduleType && setScheduleType('tomorrow');
                            const tomorrowStr = getLocalDateString(getNextBusinessDay());
                            setSelectedScheduleDate && setSelectedScheduleDate(tomorrowStr);
                            setShowScheduleDropdown && setShowScheduleDropdown(false);
                            setShowScheduleSidebar && setShowScheduleSidebar(true);
                            handleLoadScheduleData && handleLoadScheduleData();
                          }}>
                            <i className="bi bi-calendar-week" style={{ fontSize: '14px', color: '#7c4dff' }}></i>
                            <span>Programar para amanhã</span>
                          </div>
                          <div style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'var(--text-body, #2d3748)',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            borderTop: '1px solid var(--border-subtle, #e2e8f0)'
                          }}
                          className="open-os-list-item"
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onClick={() => {
                            // Minimizar "ÚLTIMO ATENDIMENTO" quando programar atendimento
                            if (setShowLastOsSection) setShowLastOsSection(false);
                            setScheduleType && setScheduleType('future');
                            setSelectedScheduleDate && setSelectedScheduleDate(null);
                            if (setScheduleNoCertainDate) setScheduleNoCertainDate(false);
                            if (setFuturaStandbyNotes) setFuturaStandbyNotes('');
                            setShowScheduleDropdown && setShowScheduleDropdown(false);
                            setShowScheduleSidebar && setShowScheduleSidebar(true);
                            handleLoadScheduleData && handleLoadScheduleData();
                          }}>
                            <i className="bi bi-calendar-event" style={{ fontSize: '14px', color: '#7c4dff' }}></i>
                            <span>Agendamento futuro</span>
                          </div>
                          <div style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            transition: 'background 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: 'var(--text-body, #2d3748)',
                            fontSize: '12px',
                            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                            borderTop: '1px solid var(--border-subtle, #e2e8f0)'
                          }}
                          className="open-os-list-item"
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onClick={() => {
                            if (setShowLastOsSection) setShowLastOsSection(false);
                            setScheduleType && setScheduleType('apenas_tecnico');
                            setSelectedScheduleDate && setSelectedScheduleDate(null);
                            setShowScheduleDropdown && setShowScheduleDropdown(false);
                            setShowScheduleSidebar && setShowScheduleSidebar(true);
                            handleLoadScheduleData && handleLoadScheduleData();
                          }}>
                            <i className="bi bi-person" style={{ fontSize: '14px', color: '#7c4dff' }}></i>
                            <span>Apenas definir técnico</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Botão Gerar OS */}
                    <button
                      type="button"
                      className="open-os-btn-primary"
                      disabled={!validateRequiredFields() || isCreatingOS}
                      onClick={() => {
                        const validation = validateRequiredFieldsWithErrors(
                          solicitante,
                          contatoFone1,
                          selectedDefeito,
                          defeitoObservacao,
                          formValidationExtras
                        );
                        if (!validation.isValid) {
                          alert('Por favor, corrija os seguintes campos:\n\n' + validation.errors.join('\n'));
                          return;
                        }
                        if (handleCreateOS) {
                          handleCreateOS(tipoOsValue);
                        } else {
                          console.log('Gerar OS - Validação passou');
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '8px 14px',
                        fontSize: '12px',
                        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                        fontWeight: 600,
                        color: validateRequiredFields() ? '#ffffff' : 'var(--text-muted, #9ca3af)',
                        background: validateRequiredFields() ? '#7c4dff' : 'var(--bg-surface-alt, #e5e7eb)',
                        border: validateRequiredFields() ? '1px solid #7c4dff' : '1px solid #d1d5db',
                        borderRadius: '6px',
                        cursor: validateRequiredFields() ? 'pointer' : 'not-allowed',
                        transition: 'all 0.2s ease',
                        boxShadow: validateRequiredFields() ? '0 2px 6px rgba(124, 77, 255, 0.35)' : 'none',
                        opacity: validateRequiredFields() ? 1 : 0.6
                      }}
                      onMouseEnter={(e) => {
                        if (validateRequiredFields()) {
                          e.target.style.background = '#5a3dc4';
                          e.target.style.borderColor = '#5a3dc4';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (validateRequiredFields()) {
                          e.target.style.background = '#7c4dff';
                          e.target.style.borderColor = '#7c4dff';
                        }
                      }}
                    >
                      <i className="bi bi-file-earmark-plus" style={{ fontSize: '14px', lineHeight: 1 }}></i>
                      <span>Gerar OS</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

      {/* Modal de Equipamentos Selecionados */}
      {showEquipmentsModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={(e) => {
            // Não fechar ao clicar no backdrop
            if (e.target === e.currentTarget) {
              // Não fazer nada - modal só fecha no X
            }
          }}
        >
          <div 
            style={{
              background: 'var(--modal-bg, #ffffff)',
              borderRadius: '12px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header do Modal */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-subtle, #e8e8e8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              height: '30px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: 'var(--text-body, #2d3748)'
              }}>
                Equipamentos Selecionados
              </h3>
              <button
                onClick={() => setShowEquipmentsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  color: 'var(--text-muted, #718096)',
                  cursor: 'pointer',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'var(--bg-surface-alt, #f7fafc)';
                  e.target.style.color = 'var(--text-body, #2d3748)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = 'var(--text-muted, #718096)';
                }}
              >
                ×
              </button>
            </div>

            {/* Corpo do Modal - Tabela */}
            <div style={{
              padding: '5px',
              overflowY: 'auto',
              flex: 1,
              maxHeight: '480px'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif"
              }}>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                }}>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderBottom: '2px solid var(--border-subtle, #e8e8e8)',
                      fontWeight: 600,
                      color: 'var(--text-body, #2d3748)',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: 'var(--bg-surface-alt, #f7fafc)'
                    }}>
                      Nome do Site
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderBottom: '2px solid var(--border-subtle, #e8e8e8)',
                      fontWeight: 600,
                      color: 'var(--text-body, #2d3748)',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: 'var(--bg-surface-alt, #f7fafc)'
                    }}>
                      Equipamento
                    </th>
                    <th style={{
                      textAlign: 'left',
                      padding: '12px 16px',
                      borderBottom: '2px solid var(--border-subtle, #e8e8e8)',
                      fontWeight: 600,
                      color: 'var(--text-body, #2d3748)',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      background: 'var(--bg-surface-alt, #f7fafc)'
                    }}>
                      Série
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEquipments && selectedEquipments.map((equipment, index) => (
                    <tr 
                      key={index}
                      style={{
                        borderBottom: index < selectedEquipments.length - 1 ? '1px solid var(--border-subtle, #f0f0f0)' : 'none',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td style={{
                        padding: '12px 16px',
                        color: 'var(--text-body, #2d3748)',
                        fontWeight: 500,
                        fontSize: '10px'
                      }}>
                        {equipment.siteNome || 'N/A'}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        color: 'var(--text-body, #2d3748)',
                        fontWeight: 500,
                        fontSize: '10px'
                      }}>
                        {equipment.modelo || 'N/A'}
                      </td>
                      <td style={{
                        padding: '12px 16px',
                        color: 'var(--text-body, #2d3748)',
                        fontWeight: 500,
                        fontSize: '10px'
                      }}>
                        {equipment.numSerie || 'N/A'} {equipment.patrimonio && `• ${equipment.patrimonio}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentInfoStep;

